import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Clock,
  ShieldCheck,
  MessageSquareText,
  ArrowUpRight,
  FileText,
} from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';

function WhatsappIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.556-5.338 11.891-11.893 11.891a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.695.247-1.29.173-1.414z" />
    </svg>
  );
}

const WHATSAPP_URL = 'https://wa.me/79894734322';
const WHATSAPP_HUMAN = '+7 989 473-43-22';
const SUPPORT_EMAIL = 'resstrosaas@mail.ru';

const QUICK_TOPICS = [
  {
    title: 'Подключение кассы',
    text: 'Как сгенерировать код пары и привязать iPad к заведению за 30 секунд.',
  },
  {
    title: 'Оплата и тарифы',
    text: 'Продление подписки, смена тарифа, закрывающие документы и счета.',
  },
  {
    title: 'Меню и модификаторы',
    text: 'Импорт позиций, стоп-листы, варианты и быстрые комментарии.',
  },
  {
    title: 'Отчёты и смены',
    text: 'Z-отчёт, выручка по сотрудникам, экспорт в 1С и Excel.',
  },
  {
    title: 'Принтеры и оборудование',
    text: 'Bluetooth-принтеры чеков, фискальные кассы и сканеры.',
  },
  {
    title: 'Безопасность доступа',
    text: 'Ролевые права, отзыв устройства из админ-панели, 2FA.',
  },
];

const FAQ = [
  {
    q: 'Сколько времени занимает настройка?',
    a: 'Аккаунт и первое заведение создаются за 5 минут. Полноценный запуск занимает в среднем 1–2 дня: импорт меню, настройка принтеров и обучение команды. Мы помогаем на каждом этапе.',
  },
  {
    q: 'Что делать, если касса не подключается к интернету?',
    a: 'Касса продолжает принимать заказы в офлайн-режиме. Все операции сохраняются локально и синхронизируются с сервером, как только сеть появится. Данные не теряются.',
  },
  {
    q: 'Можно ли отозвать доступ у утерянного iPad?',
    a: 'Да. Зайдите в админ-панель → «Устройства» → нужный iPad → «Отозвать». Устройство мгновенно потеряет доступ к заведению, даже если оно сейчас офлайн: при следующем выходе в сеть сессия закроется.',
  },
  {
    q: 'Какие принтеры поддерживаются?',
    a: 'Любые ESC/POS-принтеры с Bluetooth или по сети: Xprinter, Epson TM, Star, Bixolon. Фискальные кассы интегрируем под запрос (АТОЛ, Эвотор). Напишите в WhatsApp, подскажем совместимые модели.',
  },
  {
    q: 'Мы работаем 24/7, а ваша поддержка?',
    a: 'Поддержка в WhatsApp отвечает ежедневно с 09:00 до 22:00 МСК. Для тарифа Business и выше доступна круглосуточная дежурная линия с ответом в течение 15 минут на критичные инциденты.',
  },
];

const HOURS = [
  { label: 'Пн–Пт', value: '09:00–22:00 МСК' },
  { label: 'Сб–Вс', value: '10:00–20:00 МСК' },
  { label: 'Business 24/7', value: 'Круглосуточная дежурная линия' },
];

export default function Support() {
  return (
    <div className="min-h-screen bg-paper">
      <Header />

      <section className="section">
        <div className="container-site">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-ink/60 transition hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            На главную
          </Link>

          <div className="mt-10 grid gap-10 md:grid-cols-12">
            {/* Левая колонка, заголовок + основные каналы */}
            <div className="md:col-span-7">
              <span className="eyebrow">Поддержка</span>
              <h1 className="h2 mt-4">Мы на связи, когда вам нужно</h1>
              <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-ink/70">
                Отвечаем на вопросы про подключение кассы, настройку меню, оплату и интеграции.
                Пишите в WhatsApp, обычно отвечаем в течение 15 минут в рабочее время.
              </p>

              {/* Основной канал, WhatsApp */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-8 flex items-start gap-5 border border-line bg-white p-6 transition hover:border-ink"
              >
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center bg-[#25D366] text-white"
                  aria-hidden="true"
                >
                  <WhatsappIcon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                    Быстрее всего
                    <span className="inline-block h-1 w-1 bg-orange" />
                    среднее время ответа 15 минут
                  </div>
                  <div className="mt-2 text-[18px] font-medium text-ink">
                    WhatsApp · {WHATSAPP_HUMAN}
                  </div>
                  <div className="mt-1 text-[13px] text-ink/60">
                    Скриншоты, голосовые, звонки: всё в одном чате. Оставайтесь на связи,
                    даже если закрыли окно.
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  strokeWidth={1.75}
                  className="mt-1 shrink-0 text-ink/40 transition group-hover:text-ink"
                />
              </a>

              {/* Дополнительный канал, email */}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="group mt-4 flex items-start gap-5 border border-line bg-white p-6 transition hover:border-ink"
              >
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center border border-line bg-paper text-ink/70"
                  aria-hidden="true"
                >
                  <Mail size={18} strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                    Для документов и договоров
                  </div>
                  <div className="mt-2 text-[18px] font-medium text-ink">{SUPPORT_EMAIL}</div>
                  <div className="mt-1 text-[13px] text-ink/60">
                    Закрывающие документы, счета, бухгалтерские запросы. Отвечаем в течение
                    рабочего дня.
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  strokeWidth={1.75}
                  className="mt-1 shrink-0 text-ink/40 transition group-hover:text-ink"
                />
              </a>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark inline-flex items-center gap-2"
                >
                  <WhatsappIcon className="h-4 w-4" />
                  Написать в WhatsApp
                </a>
                <Link to="/cabinet" className="btn-outline">
                  В личный кабинет
                </Link>
              </div>
            </div>

            {/* Правая колонка, часы работы + гарантии */}
            <div className="md:col-span-5">
              <div className="relative overflow-hidden border border-line bg-white">
                <div className="flex items-center justify-between border-b border-line bg-paper/60 px-6 py-4">
                  <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/55">
                    <Clock size={13} strokeWidth={1.75} />
                    Режим работы
                  </div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/40">
                    МСК · UTC+3
                  </div>
                </div>

                <ul className="divide-y divide-line">
                  {HOURS.map((h) => (
                    <li
                      key={h.label}
                      className="flex items-center justify-between gap-4 px-6 py-4"
                    >
                      <span className="text-[13px] font-medium text-ink">{h.label}</span>
                      <span className="text-right text-[13px] text-ink/65">{h.value}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-line bg-orange px-6 py-5 text-white">
                  <div className="flex items-start gap-3 text-[13px] leading-relaxed">
                    <ShieldCheck size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
                    <div>
                      В дни критичных инцидентов (падение интернета, проблема с принтером
                      в час пик), пишите в WhatsApp. Мы на связи даже вне графика.
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-[11px] leading-relaxed text-ink/45">
                Мы не делаем холодных звонков и не запрашиваем пароли. Сотрудник поддержки
                представляется по имени и ссылается на ваше заведение в Resstro.
              </p>

              {/* Реквизиты ИП, для договоров и счетов */}
              <div className="mt-6 border border-line bg-white">
                <div className="flex items-center justify-between border-b border-line bg-paper/60 px-6 py-4">
                  <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/55">
                    <FileText size={13} strokeWidth={1.75} />
                    Юридические реквизиты
                  </div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/40">
                    Для договоров
                  </div>
                </div>
                <dl className="divide-y divide-line">
                  <div className="grid grid-cols-[110px_1fr] items-baseline gap-4 px-6 py-3">
                    <dt className="text-[12px] uppercase tracking-[0.08em] text-ink/45">Форма</dt>
                    <dd className="text-[13px] text-ink">ИП Раджабов Рустам Эфлетдинович</dd>
                  </div>
                  <div className="grid grid-cols-[110px_1fr] items-baseline gap-4 px-6 py-3">
                    <dt className="text-[12px] uppercase tracking-[0.08em] text-ink/45">ИНН</dt>
                    <dd className="text-[13px] tabular-nums text-ink">051707862851</dd>
                  </div>
                  <div className="grid grid-cols-[110px_1fr] items-baseline gap-4 px-6 py-3">
                    <dt className="text-[12px] uppercase tracking-[0.08em] text-ink/45">Email</dt>
                    <dd className="text-[13px]">
                      <a
                        href={`mailto:${SUPPORT_EMAIL}`}
                        className="text-ink hover:text-orange transition-colors"
                      >
                        {SUPPORT_EMAIL}
                      </a>
                    </dd>
                  </div>
                  <div className="grid grid-cols-[110px_1fr] items-baseline gap-4 px-6 py-3">
                    <dt className="text-[12px] uppercase tracking-[0.08em] text-ink/45">Телефон</dt>
                    <dd className="text-[13px]">
                      <a href="tel:+79894734322" className="text-ink hover:text-orange transition-colors">
                        {WHATSAPP_HUMAN}
                      </a>
                    </dd>
                  </div>
                </dl>
                <div className="border-t border-line bg-paper/40 px-6 py-3 text-[11px] leading-relaxed text-ink/55">
                  Закрывающие документы, счёт и&nbsp;договор оферты выписываем по&nbsp;запросу
                  на&nbsp;{SUPPORT_EMAIL}.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Популярные темы */}
      <section className="section-divider bg-white">
        <div className="container-site section">
          <div className="flex items-end justify-between gap-6">
            <div>
              <span className="eyebrow">Популярные темы</span>
              <h2 className="h2 mt-4 max-w-2xl">С чем чаще всего обращаются</h2>
            </div>
          </div>

          <ul className="mt-10 grid gap-0 border-t border-line md:grid-cols-2 md:border-t-0">
            {QUICK_TOPICS.map((t, i) => (
              <li
                key={t.title}
                className={
                  'flex items-start gap-4 border-b border-line py-5 ' +
                  (i % 2 === 0 ? 'md:border-r md:pr-8' : 'md:pl-8')
                }
              >
                <MessageSquareText
                  size={16}
                  strokeWidth={1.75}
                  className="mt-1 shrink-0 text-orange"
                />
                <div>
                  <div className="text-[15px] font-medium text-ink">{t.title}</div>
                  <div className="mt-1 text-[13px] leading-relaxed text-ink/65">{t.text}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-3 text-[13px] text-ink/60">
            Не нашли свой вопрос?
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="link inline-flex items-center gap-1.5"
            >
              <WhatsappIcon className="h-3.5 w-3.5 text-[#25D366]" />
              Напишите в WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-divider bg-paper">
        <div className="container-site section">
          <span className="eyebrow">Частые вопросы</span>
          <h2 className="h2 mt-4 max-w-2xl">Коротко о главном</h2>

          <div className="mt-10 border-t border-line">
            {FAQ.map((item) => (
              <details
                key={item.q}
                className="group border-b border-line py-5 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer list-none items-start justify-between gap-6">
                  <span className="text-[16px] font-medium text-ink">{item.q}</span>
                  <span
                    aria-hidden="true"
                    className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center border border-line text-ink/60 transition group-open:rotate-45 group-open:border-ink group-open:text-ink"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 max-w-3xl text-[14px] leading-relaxed text-ink/70">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
